import { db, Voucher, VoucherEntry, InventoryEntry } from '../db/db';

export class ErpError extends Error {
  code: string;
  context?: any;

  constructor(message: string, code: string, context?: any) {
    super(message);
    this.name = 'ErpError';
    this.code = code;
    this.context = context;
  }
}

export class AccountingEngine {
  /**
   * Validates if a voucher's entries balance (Total Dr = Total Cr)
   */
  static validateVoucher(entries: Partial<VoucherEntry>[]): boolean {
    let totalDr = 0;
    let totalCr = 0;

    for (const entry of entries) {
      if (entry.type === 'Dr') totalDr += entry.amount || 0;
      if (entry.type === 'Cr') totalCr += entry.amount || 0;
    }

    return Math.abs(totalDr - totalCr) < 0.01;
  }

  /**
   * Gets the next auto-incrementing system voucher number for a specific type
   */
  static async getNextVoucherNumber(companyId: number, type: string): Promise<string> {
    const voucherType = await db.voucherTypes.where('[companyId+name]').equals([companyId, type]).first();

    if (voucherType) {
      return voucherType.currentNumber.toString();
    }

    // Fallback for system types if not seeded properly yet
    const vouchers = await db.vouchers
      .where('companyId').equals(companyId)
      .toArray();
      
    const deletedVouchers = await db.deletedVouchers
      .where('companyId').equals(companyId)
      .toArray();
    
    const extractNum = (str: string) => parseInt((str || '').replace(/\D/g, ''), 10);
    const filteredVouchers = vouchers.filter(v => v.type === type);
    const deletedForType = deletedVouchers.filter(v => v.type === type);
    
    const validNums = [...filteredVouchers, ...deletedForType].map(v => extractNum(v.voucherNumber)).filter(n => !isNaN(n));
    if (validNums.length === 0) return '1';
    
    return (Math.max(...validNums) + 1).toString();
  }

  static async validateDate(companyId: number, date: string) {
    const company = await db.companies.get(companyId);
    if (!company) throw new ErpError('Company not found.', 'ERR_COMPANY_NOT_FOUND', { companyId });
    
    // date is YYYY-MM-DD
    const booksBeginFrom = company.booksBeginFrom;
    
    // Create an end of financial year date string
    const [year, month, day] = company.financialYearStart.split('-');
    const fyEndYear = parseInt(year) + 1;
    // Aarthika's financial year ends strictly before the start of the next year.
    // To strictly compare, we can just say date must be < fyEnd boundary.
    // Next year's same day is the upper bound (exclusive).
    const fyEndBoundary = `${fyEndYear}-${month}-${day}`;
    
    if (date < booksBeginFrom) {
       throw new ErpError(`Invalid Date: Voucher date cannot be before Books Begin From date (${company.booksBeginFrom}).`, 'ERR_INVALID_DATE_PAST', { date, booksBeginFrom: company.booksBeginFrom });
    }
    
    if (date >= fyEndBoundary) {
       throw new ErpError(`Invalid Date: Voucher date cannot be outside the current Financial Year.`, 'ERR_INVALID_DATE_FUTURE_FY', { date, fyEndBoundary });
    }
  }

  /**
   * Posts a new voucher and its entries to the database
   */
  static async postVoucher(
    voucher: Omit<Voucher, 'id' | 'voucherNumber'> & { voucherNumber?: string }, 
    entries: Omit<VoucherEntry, 'id' | 'voucherId'>[],
    inventoryEntries: Omit<InventoryEntry, 'id' | 'voucherId'>[] = [],
    billAllocations?: any[]
  ): Promise<number> {
    await this.validateDate(voucher.companyId, voucher.date);

    if (!this.validateVoucher(entries)) {
      throw new ErpError('Voucher entries do not balance. Total Debits must equal Total Credits.', 'ERR_VOUCHER_IMBALANCE', { entries });
    }

    const maxRetries = 2;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        return await db.transaction(
          'rw', 
          [db.vouchers, db.voucherEntries, db.inventoryEntries, db.voucherTypes, db.auditLogs, db.deletedVouchers], 
          async () => {
            // 1. Resolve Voucher Type ID
            const voucherType = await db.voucherTypes.where('[companyId+name]').equals([voucher.companyId, voucher.type]).first();
            
            let finalVoucherNumber = '';
      
            if (voucherType) {
              finalVoucherNumber = voucherType.currentNumber.toString();
              
              await db.voucherTypes.update(voucherType.id!, {
                currentNumber: voucherType.currentNumber + 1
              });
            } else {
               // Fallback inside transaction for auto-increment if no voucher type is found
               const vouchers = await db.vouchers.where('companyId').equals(voucher.companyId).filter(v => v.type === voucher.type).toArray();
               const deletedVouchers = await db.deletedVouchers.where('companyId').equals(voucher.companyId).filter(v => v.type === voucher.type).toArray();
               
               const extractNum = (str: string) => parseInt((str || '').replace(/\D/g, ''), 10);
               const validNums = [...vouchers, ...deletedVouchers].map(v => extractNum(v.voucherNumber)).filter(n => !isNaN(n));
               
               const nextNum = validNums.length === 0 ? 1 : Math.max(...validNums) + 1;
               finalVoucherNumber = nextNum.toString();
            }
            
            // System-Generated Voucher Number Duplicate Validation
            const existingSysMatch = await db.vouchers
                .where('companyId').equals(voucher.companyId)
                .filter(v => v.type === voucher.type && v.voucherNumber === finalVoucherNumber)
                .first();
                
            if (existingSysMatch) {
               throw new ErpError(`Duplicate Check Failed: System Generated ID '${finalVoucherNumber}' already exists for type '${voucher.type}'.`, 'ERR_DUPLICATE_SYS_ID', { finalVoucherNumber, type: voucher.type });
            }
      
            const finalVoucher = { 
              ...voucher, 
              voucherNumber: finalVoucherNumber,
              voucherTypeId: voucherType?.id,
              ...(billAllocations && billAllocations.length > 0 ? { billAllocations } : {})
            };
            
            const voucherId = await db.vouchers.add(finalVoucher as Voucher);
            
            const entriesToInsert = entries.map(entry => ({
              ...entry,
              voucherId
            }));
      
            await db.voucherEntries.bulkAdd(entriesToInsert as VoucherEntry[]);
      
            if (inventoryEntries.length > 0) {
              const invEntriesToInsert = inventoryEntries.map(ie => ({
                ...ie,
                voucherId
              }));
              await db.inventoryEntries.bulkAdd(invEntriesToInsert as InventoryEntry[]);
            }
            
            // Audit Log
            await db.auditLogs.add({
               companyId: voucher.companyId,
               actionType: 'CREATE_VOUCHER',
               entityType: 'voucher',
               entityId: voucherId as number,
               timestamp: new Date().toISOString(),
               details: `Created voucher ${finalVoucherNumber} of type ${voucher.type}`
            });
      
            return voucherId as number;
        });
      } catch (err: any) {
        attempt++;
        if (
          attempt >= maxRetries || 
          err.message.includes('Duplicate Check Failed') || 
          err.message.includes('do not balance')
        ) {
          throw err;
        }
      }
    }
    throw new Error('Failed to post voucher after multiple attempts.');
  }

  /**
   * Updates an existing voucher and replaces its entries
   */
  static async updateVoucher(
    voucherId: number, 
    voucher: Partial<Voucher>, 
    entries: Omit<VoucherEntry, 'id' | 'voucherId'>[],
    inventoryEntries: Omit<InventoryEntry, 'id' | 'voucherId'>[] = [],
    billAllocations?: any[]
  ): Promise<number> {
    if (voucher.companyId && voucher.date) {
      await this.validateDate(voucher.companyId, voucher.date);
    } else if (voucher.date) {
      const oldVoucher = await db.vouchers.get(voucherId);
      if (oldVoucher) await this.validateDate(oldVoucher.companyId, voucher.date);
    }

    if (!this.validateVoucher(entries)) {
      throw new ErpError('Voucher entries do not balance. Total Debits must equal Total Credits.', 'ERR_VOUCHER_IMBALANCE', { entries });
    }

    const maxRetries = 2;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        return await db.transaction(
          'rw', 
          [db.vouchers, db.voucherEntries, db.inventoryEntries, db.auditLogs], 
          async () => {
            const oldVoucher = await db.vouchers.get(voucherId);
            if (!oldVoucher) throw new ErpError('Voucher not found.', 'ERR_VOUCHER_NOT_FOUND', { voucherId });

            // Prevent changing voucher type without updating ID in a safe manner
            let finalType = voucher.type || oldVoucher.type;
            const companyId = voucher.companyId || oldVoucher.companyId;

            // Ensure voucherNumber is strictly immutable while allowing referenceNumber updates
            const updatePayload: any = { ...voucher };
            delete updatePayload.sysId; // Clean up old legacy prop if passed accidentally
            updatePayload.voucherNumber = oldVoucher.voucherNumber;
            if (billAllocations !== undefined) {
              updatePayload.billAllocations = billAllocations.length > 0 ? billAllocations : undefined;
            }
      
            await db.vouchers.update(voucherId, updatePayload);
            
            // Delete old entries
            await db.voucherEntries.where('voucherId').equals(voucherId).delete();
            await db.inventoryEntries.where('voucherId').equals(voucherId).delete();
            
            // Insert new entries
            const entriesToInsert = entries.map(entry => ({
              ...entry,
              voucherId
            }));
            await db.voucherEntries.bulkAdd(entriesToInsert as VoucherEntry[]);
      
            if (inventoryEntries.length > 0) {
              const invEntriesToInsert = inventoryEntries.map(ie => ({
                ...ie,
                voucherId
              }));
              await db.inventoryEntries.bulkAdd(invEntriesToInsert as InventoryEntry[]);
            }
            
            // Audit Log
            await db.auditLogs.add({
               companyId: companyId,
               actionType: 'UPDATE_VOUCHER',
               entityType: 'voucher',
               entityId: voucherId as number,
               timestamp: new Date().toISOString(),
               details: `Updated voucher ${oldVoucher.voucherNumber} of type ${finalType}`
            });
      
            return voucherId;
        });
      } catch (err: any) {
        attempt++;
        if (
          attempt >= maxRetries || 
          err.message.includes('Duplicate Check Failed') || 
          err.message.includes('do not balance') ||
          err.message.includes('Voucher not found')
        ) {
          throw err;
        }
      }
    }
    throw new Error('Failed to update voucher after multiple attempts.');
  }

  /**
   * Deletes a voucher and its entries safely with audit logs
   */
  static async deleteVoucher(voucherId: number, companyId: number): Promise<void> {
    return await db.transaction('rw', [db.vouchers, db.voucherEntries, db.inventoryEntries, db.deletedVouchers, db.auditLogs], async () => {
       const v = await db.vouchers.get(voucherId);
       if (!v) throw new Error('Voucher not found for deletion.');

       const now = new Date().toISOString();

       // Soft-delete the voucher
       await db.vouchers.update(voucherId, { 
          deleted: true, 
          deletedAt: now,
          deletedBy: 'System'
       });

       // Record in deletedVouchers so getNextVoucherNumber() can skip this number
       await db.deletedVouchers.add({
          companyId,
          voucherTypeId: v.voucherTypeId ?? 0,
          type: v.type,
          voucherNumber: v.voucherNumber,
          referenceNumber: v.referenceNumber,
          sysId: v.sysId,
          deletedAt: now,
       });

       await db.auditLogs.add({
          companyId,
          actionType: 'DELETE_VOUCHER',
          entityType: 'voucher',
          entityId: voucherId,
          timestamp: now,
          details: `Deleted voucher ${v.voucherNumber} of type ${v.type}`
       });
    });
  }

  /**
   * Resets voucher numbering for all auto-numbered voucher types back to their startingNumber.
   * Call this when opening a new Financial Year for a company.
   * Safe to call multiple times — it is idempotent (sets to startingNumber, not 1).
   */
  static async resetVoucherNumbersForNewFY(companyId: number): Promise<void> {
    const voucherTypes = await db.voucherTypes
      .where('companyId').equals(companyId)
      .toArray();

    await db.transaction('rw', [db.voucherTypes, db.auditLogs], async () => {
      for (const vt of voucherTypes) {
        if (vt.numberingMethod === 'auto') {
          await db.voucherTypes.update(vt.id!, {
            currentNumber: vt.startingNumber ?? 1
          });
        }
      }

      await db.auditLogs.add({
        companyId,
        actionType: 'UPDATE' as any,
        entityType: 'voucherType',
        entityId: companyId,
        timestamp: new Date().toISOString(),
        details: `Reset voucher numbering for new Financial Year (companyId: ${companyId})`
      });
    });
  }

  /**
   * Calculates the closing balance (or period movement) of a specific ledger up to a given date.
   * If fromDate is not provided, it acts as a cumulative closing balance.
   */
  static async getLedgerBalance(ledgerId: number, fromDate?: string, toDate?: string, preloadedCurrencies?: Map<number, any>): Promise<{ balance: number, type: 'Dr' | 'Cr' }> {
    const ledger = await db.ledgers.get(ledgerId);
    if (!ledger) throw new Error('Ledger not found');

    const company = await db.companies.get(ledger.companyId);

    let currencyMap = preloadedCurrencies;
    if (!currencyMap) {
      const currencies = await db.currencies.where('companyId').equals(ledger.companyId).toArray();
      currencyMap = new Map(currencies.map(c => [c.id, c]));
    }

    let totalDr = 0;
    let totalCr = 0;

    let includeOpening = true;
    if (fromDate && company && company.financialYearStart) {
       if (fromDate > company.financialYearStart) {
          includeOpening = false;
       }
    }

    if (includeOpening) {
      totalDr = ledger.balanceType === 'Dr' ? ledger.openingBalance : 0;
      totalCr = ledger.balanceType === 'Cr' ? ledger.openingBalance : 0;
    }

    const entries = await db.voucherEntries.where('ledgerId').equals(ledgerId).toArray();
    if (entries.length > 0) {
      const voucherIds = [...new Set(entries.map(e => e.voucherId))];
      const vouchers = await db.vouchers.where('id').anyOf(voucherIds).toArray();
      const voucherMap = new Map(vouchers.map(v => [v.id, v]));

      for (const entry of entries) {
        const voucher = voucherMap.get(entry.voucherId);
        if (!voucher || voucher.deleted) continue;
        
        if (fromDate && voucher.date < fromDate) continue;
        if (toDate && voucher.date > toDate) continue;

        let finalAmount = entry.amount;
        const curId = entry.currencyId || voucher.currencyId;
        if (curId) {
          const fx = currencyMap.get(curId);
          if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
             finalAmount = entry.amount * fx.exchangeRate;
          }
        }

        if (entry.type === 'Dr') totalDr += finalAmount;
        if (entry.type === 'Cr') totalCr += finalAmount;
      }
    }

    const netBalance = totalDr - totalCr;
    
    if (netBalance >= 0) {
      return { balance: netBalance, type: 'Dr' };
    } else {
      return { balance: Math.abs(netBalance), type: 'Cr' };
    }
  }

  /**
   * Fast, single-pass optimized ledger balance engine. O(1) query per entire company.
   */
  static async getCompanyBalancesFast(companyId: number, fromDate?: string, toDate?: string): Promise<Map<number, { balance: number, type: 'Dr' | 'Cr' }>> {
    const ledgers = (await db.ledgers.where('companyId').equals(companyId).toArray()).filter(l => !l.isDeleted);
    const currencies = await db.currencies.where('companyId').equals(companyId).toArray();
    const currencyMap = new Map(currencies.map(c => [c.id, c]));
    const company = await db.companies.get(companyId);

    const balances = new Map<number, { totalDr: number, totalCr: number, balanceType: 'Dr' | 'Cr' }>();
    
    // Initialize open balances
    for (const l of ledgers) {
      let totalDr = 0;
      let totalCr = 0;
      let includeOpening = true;
      if (fromDate && company && company.financialYearStart) {
         if (fromDate > company.financialYearStart) includeOpening = false;
      }
      if (includeOpening) {
         totalDr = l.balanceType === 'Dr' ? l.openingBalance : 0;
         totalCr = l.balanceType === 'Cr' ? l.openingBalance : 0;
      }
      balances.set(l.id!, { totalDr, totalCr, balanceType: l.balanceType });
    }

    const vouchers = await db.vouchers.where('companyId').equals(companyId).toArray();
    const validVouchers = vouchers.filter(v => !v.deleted && (!fromDate || v.date >= fromDate) && (!toDate || v.date <= toDate));
    const validVoucherIds = new Set(validVouchers.map(v => v.id));
    const voucherMap = new Map(validVouchers.map(v => [v.id, v]));

    if (validVoucherIds.size > 0) {
       const ledgerIds = ledgers.map(l => l.id!);
       const allEntries = await db.voucherEntries.where('ledgerId').anyOf(ledgerIds).toArray();
       
       for (const entry of allEntries) {
          if (!validVoucherIds.has(entry.voucherId)) continue;
          
          const voucher = voucherMap.get(entry.voucherId)!;
          let finalAmount = entry.amount;
          const curId = entry.currencyId || voucher.currencyId;
          if (curId) {
             const fx = currencyMap.get(curId);
             if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
                finalAmount = entry.amount * fx.exchangeRate;
             }
          }

          const bal = balances.get(entry.ledgerId);
          if (bal) {
             if (entry.type === 'Dr') bal.totalDr += finalAmount;
             if (entry.type === 'Cr') bal.totalCr += finalAmount;
          }
       }
    }

    const resultMap = new Map<number, { balance: number, type: 'Dr' | 'Cr' }>();
    for (const [id, bal] of balances.entries()) {
       let currDr = bal.totalDr;
       let currCr = bal.totalCr;
       if (currDr > currCr) resultMap.set(id, { balance: currDr - currCr, type: 'Dr' });
       else if (currCr > currDr) resultMap.set(id, { balance: currCr - currDr, type: 'Cr' });
       else resultMap.set(id, { balance: 0, type: bal.balanceType });
    }
    
    return resultMap;
  }

  /**
   * Generates the Balance Sheet and P&L data for a company
   */
  static async generateBalanceSheet(companyId: number, fromDate?: string, toDate?: string) {
    const groups = (await db.groups.where('companyId').equals(companyId).toArray()).filter(g => !g.isDeleted);
    const ledgers = (await db.ledgers.where('companyId').equals(companyId).toArray()).filter(l => !l.isDeleted);
    
    // Calculate all ledger balances first
    const ledgerBalances = await this.getCompanyBalancesFast(companyId, undefined, toDate);

    // Helper to recursively calculate group balance
    const getGroupTotal = (groupId: number, visited = new Set<number>()): { dr: number, cr: number } => {
      if (visited.has(groupId)) return { dr: 0, cr: 0 };
      visited.add(groupId);
      
      let dr = 0;
      let cr = 0;

      // Add direct ledgers
      const childLedgers = ledgers.filter(l => l.groupId === groupId);
      for (const l of childLedgers) {
        const bal = ledgerBalances.get(l.id!);
        if (bal) {
          if (bal.type === 'Dr') dr += bal.balance;
          if (bal.type === 'Cr') cr += bal.balance;
        }
      }

      // Add child groups
      const childGroups = groups.filter(g => g.parentGroupId === groupId);
      for (const g of childGroups) {
        const childBal = getGroupTotal(g.id!, visited);
        dr += childBal.dr;
        cr += childBal.cr;
      }

      return { dr, cr };
    };

    const assets: any[] = [];
    const liabilities: any[] = [];
    let totalIncomeCr = 0;
    let totalIncomeDr = 0;
    let totalExpenseDr = 0;
    let totalExpenseCr = 0;

    // Process Primary Groups
    const primaryGroups = groups.filter(g => g.parentGroupId === null);
    
    for (const group of primaryGroups) {
      const { dr, cr } = getGroupTotal(group.id!);
      const net = dr - cr;
      
      if (group.nature === 'Assets') {
        if (net !== 0) assets.push({ id: group.id, isGroup: true, name: group.name, amount: Math.abs(net), type: net >= 0 ? 'Dr' : 'Cr' });
      } else if (group.nature === 'Liabilities') {
        if (net !== 0) liabilities.push({ id: group.id, isGroup: true, name: group.name, amount: Math.abs(net), type: net <= 0 ? 'Cr' : 'Dr' });
      } else if (group.nature === 'Income') {
        totalIncomeDr += dr;
        totalIncomeCr += cr;
      } else if (group.nature === 'Expenses') {
        totalExpenseDr += dr;
        totalExpenseCr += cr;
      }
    }

    // Calculate P&L
    const netIncome = totalIncomeCr - totalIncomeDr;
    const netExpense = totalExpenseDr - totalExpenseCr;
    const profit = netIncome - netExpense;

    if (profit > 0) {
      liabilities.push({ id: 'pl', isGroup: false, name: 'Profit & Loss A/c', amount: profit, type: 'Cr' });
    } else if (profit < 0) {
      assets.push({ id: 'pl', isGroup: false, name: 'Profit & Loss A/c', amount: Math.abs(profit), type: 'Dr' });
    }

    // Calculate Totals
    const totalAssets = assets.reduce((sum, item) => sum + (item.type === 'Dr' ? item.amount : -item.amount), 0);
    const totalLiabilities = liabilities.reduce((sum, item) => sum + (item.type === 'Cr' ? item.amount : -item.amount), 0);

    return {
      assets,
      liabilities,
      totalAssets: Math.abs(totalAssets),
      totalLiabilities: Math.abs(totalLiabilities)
    };
  }

  /**
   * Generates the Profit & Loss A/c data for a company
   */
  static async generateProfitLoss(companyId: number, fromDate?: string, toDate?: string) {
    const groups = (await db.groups.where('companyId').equals(companyId).toArray()).filter(g => !g.isDeleted);
    const ledgers = (await db.ledgers.where('companyId').equals(companyId).toArray()).filter(l => !l.isDeleted);
    
    const ledgerBalances = await this.getCompanyBalancesFast(companyId, fromDate, toDate);

    const getGroupTotal = (groupId: number, visited = new Set<number>()): { dr: number, cr: number } => {
      if (visited.has(groupId)) return { dr: 0, cr: 0 };
      visited.add(groupId);

      let dr = 0;
      let cr = 0;

      const childLedgers = ledgers.filter(l => l.groupId === groupId);
      for (const l of childLedgers) {
        const bal = ledgerBalances.get(l.id!);
        if (bal) {
          if (bal.type === 'Dr') dr += bal.balance;
          if (bal.type === 'Cr') cr += bal.balance;
        }
      }

      const childGroups = groups.filter(g => g.parentGroupId === groupId);
      for (const g of childGroups) {
        const childBal = getGroupTotal(g.id!, visited);
        dr += childBal.dr;
        cr += childBal.cr;
      }

      return { dr, cr };
    };

    const expenses: any[] = [];
    const incomes: any[] = [];
    let totalExpenseDr = 0;
    let totalExpenseCr = 0;
    let totalIncomeDr = 0;
    let totalIncomeCr = 0;

    const primaryGroups = groups.filter(g => g.parentGroupId === null);
    
    for (const group of primaryGroups) {
      const { dr, cr } = getGroupTotal(group.id!);
      const net = dr - cr;
      
      if (group.nature === 'Expenses') {
        if (net !== 0) expenses.push({ id: group.id, isGroup: true, name: group.name, amount: Math.abs(net), type: net >= 0 ? 'Dr' : 'Cr' });
        totalExpenseDr += dr;
        totalExpenseCr += cr;
      } else if (group.nature === 'Income') {
        if (net !== 0) incomes.push({ id: group.id, isGroup: true, name: group.name, amount: Math.abs(net), type: net <= 0 ? 'Cr' : 'Dr' });
        totalIncomeDr += dr;
        totalIncomeCr += cr;
      }
    }

    const netExpense = totalExpenseDr - totalExpenseCr;
    const netIncome = totalIncomeCr - totalIncomeDr;
    const profit = netIncome - netExpense;

    if (profit > 0) {
      expenses.push({ id: 'pl_net', isGroup: false, name: 'Nett Profit', amount: profit, type: 'Dr' });
    } else if (profit < 0) {
      incomes.push({ id: 'pl_net', isGroup: false, name: 'Nett Loss', amount: Math.abs(profit), type: 'Cr' });
    }

    const totalExpenses = expenses.reduce((sum, item) => sum + (item.type === 'Dr' ? item.amount : -item.amount), 0);
    const totalIncomes = incomes.reduce((sum, item) => sum + (item.type === 'Cr' ? item.amount : -item.amount), 0);

    return {
      expenses,
      incomes,
      totalExpenses: Math.abs(totalExpenses),
      totalIncomes: Math.abs(totalIncomes)
    };
  }

  /**
   * Generates the Trial Balance data for a company
   */
  static async generateTrialBalance(companyId: number, fromDate?: string, toDate?: string) {
    const groups = (await db.groups.where('companyId').equals(companyId).toArray()).filter(g => !g.isDeleted);
    const ledgers = (await db.ledgers.where('companyId').equals(companyId).toArray()).filter(l => !l.isDeleted);

    const ledgerBalances = await this.getCompanyBalancesFast(companyId, fromDate, toDate);

    const getGroupTotal = (groupId: number, visited = new Set<number>()): { dr: number, cr: number } => {
      if (visited.has(groupId)) return { dr: 0, cr: 0 };
      visited.add(groupId);

      let dr = 0;
      let cr = 0;

      const childLedgers = ledgers.filter(l => l.groupId === groupId);
      for (const l of childLedgers) {
        const bal = ledgerBalances.get(l.id!);
        if (bal) {
          if (bal.type === 'Dr') dr += bal.balance;
          if (bal.type === 'Cr') cr += bal.balance;
        }
      }

      const childGroups = groups.filter(g => g.parentGroupId === groupId);
      for (const g of childGroups) {
        const childBal = getGroupTotal(g.id!, visited);
        dr += childBal.dr;
        cr += childBal.cr;
      }

      return { dr, cr };
    };

    const items: any[] = [];
    let totalDebit = 0;
    let totalCredit = 0;

    const primaryGroups = groups.filter(g => g.parentGroupId === null);
    
    for (const group of primaryGroups) {
      const { dr, cr } = getGroupTotal(group.id!);
      const net = dr - cr;
      
      if (Math.abs(net) > 0.001) {
        if (net > 0) {
          items.push({ id: group.id, isGroup: true, name: group.name, debit: net, credit: 0 });
          totalDebit += net;
        } else {
          items.push({ id: group.id, isGroup: true, name: group.name, debit: 0, credit: Math.abs(net) });
          totalCredit += Math.abs(net);
        }
      }
    }

    const diff = totalDebit - totalCredit;
    if (Math.abs(diff) > 0.01) {
       if (diff > 0) {
          items.push({ id: 'diff', isGroup: false, name: 'Difference in Opening Balances', debit: 0, credit: diff });
          totalCredit += diff;
       } else {
          items.push({ id: 'diff', isGroup: false, name: 'Difference in Opening Balances', debit: Math.abs(diff), credit: 0 });
          totalDebit += Math.abs(diff);
       }
    }

    return {
      items,
      totalDebit,
      totalCredit
    };
  }

  /**
   * Generates the Stock Summary data for a company, optionally filtered by group
   */
  static async generateStockSummary(companyId: number, toDate?: string, stockGroupId: number | null = null) {
    const stockGroups = (await db.stockGroups.where('companyId').equals(companyId).toArray()).filter(g => !g.isDeleted);
    const stockItems = (await db.stockItems.where('companyId').equals(companyId).toArray()).filter(i => !i.isDeleted);
    const units = (await db.units.where('companyId').equals(companyId).toArray()).filter(u => !u.isDeleted);
    const unitMap = new Map(units.map(u => [u.id, u]));
    
    const voucherTypes = await db.voucherTypes.where('companyId').equals(companyId).toArray();
    const voucherTypeMap = new Map(voucherTypes.map(vt => [vt.name, vt]));

    const currencies = await db.currencies.where('companyId').equals(companyId).toArray();
    const currencyMap = new Map(currencies.map(c => [c.id, c]));

    const getItemBalance = async (itemId: number) => {
      const item = stockItems.find(i => i.id === itemId);
      if (!item) return { quantity: 0, value: 0 };

      let totalQty = item.openingBalance;
      let totalValue = item.openingValue;

      const entries = await db.inventoryEntries.where('stockItemId').equals(itemId).toArray();
      if (entries.length > 0) {
        const voucherIds = [...new Set(entries.map(e => e.voucherId))];
        const vouchers = await db.vouchers.where('id').anyOf(voucherIds).toArray();
        const voucherMap = new Map(vouchers.map(v => [v.id, v]));

        for (const entry of entries) {
          const voucher = voucherMap.get(entry.voucherId);
          if (!voucher || voucher.deleted) continue;
          if (toDate && voucher.date > toDate) continue;

          let entryAmount = entry.amount;
          const curId = entry.currencyId || voucher.currencyId;
          if (curId) {
            const fx = currencyMap.get(curId);
            if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
              entryAmount = entry.amount * fx.exchangeRate;
            }
          }

          const vType = voucherTypeMap.get(voucher.type);
          if (vType && vType.affectsStock) {
            if (vType.stockDirection === 'In') {
              totalQty += entry.quantity;
              totalValue += entryAmount;
            } else if (vType.stockDirection === 'Out') {
              totalQty -= entry.quantity;
              totalValue -= entryAmount;
            }
          } else if (!vType) {
            // Fallback for legacy hardcoded types if not strictly found in DB
            if (voucher.type === 'Purchase' || voucher.type === 'Receipt') {
              totalQty += entry.quantity;
              totalValue += entryAmount;
            } else if (voucher.type === 'Sales' || voucher.type === 'Payment') {
              totalQty -= entry.quantity;
              totalValue -= entryAmount; 
            }
          }
        }
      }

      return { quantity: totalQty, value: totalValue };
    };

    const getGroupTotal = async (groupId: number, visited = new Set<number>()): Promise<{ quantity: number, value: number }> => {
      if (visited.has(groupId)) return { quantity: 0, value: 0 };
      visited.add(groupId);

      let quantity = 0;
      let value = 0;

      const subGroups = stockGroups.filter(g => g.parentGroupId === groupId);
      for (const sg of subGroups) {
        const total = await getGroupTotal(sg.id!, visited);
        quantity += total.quantity;
        value += total.value;
      }

      const groupItems = stockItems.filter(i => i.stockGroupId === groupId);
      for (const item of groupItems) {
        const bal = await getItemBalance(item.id!);
        quantity += bal.quantity;
        value += bal.value;
      }

      return { quantity, value };
    };

    const items = [];
    let totalValue = 0;
    const filteredGroups = stockGroups.filter(g => g.parentGroupId === stockGroupId);
    for (const g of filteredGroups) {
      const total = await getGroupTotal(g.id!);
      if (total.quantity !== 0 || total.value !== 0) {
        items.push({
          id: g.id,
          name: g.name,
          quantity: total.quantity,
          value: total.value,
          isGroup: true
        });
        totalValue += total.value;
      }
    }

    const filteredItems = stockItems.filter(i => i.stockGroupId === stockGroupId);
    for (const item of filteredItems) {
      const bal = await getItemBalance(item.id!);
      if (bal.quantity !== 0 || bal.value !== 0) {
        items.push({
          id: item.id,
          name: item.name,
          quantity: bal.quantity,
          value: bal.value,
          unit: unitMap.get(item.unitId)?.symbol || '',
          isGroup: false
        });
        totalValue += bal.value;
      }
    }

    return {
      items,
      totalValue
    };
  }

  /**
   * Generates a monthly summary for a specific voucher type (Register Summary)
   */
  static async generateRegisterSummary(companyId: number, voucherType: string, fromDate: string, toDate: string) {
    const allVouchers = await db.vouchers
      .where('companyId').equals(companyId)
      .filter(v => v.type === voucherType && !v.deleted && v.date >= fromDate && v.date <= toDate)
      .toArray();
    const filtered = allVouchers;

    const currencies = await db.currencies.where('companyId').equals(companyId).toArray();
    const currencyMap = new Map(currencies.map(c => [c.id, c]));

    // Batch-load all entries for all vouchers at once (fix N+1)
    const allVoucherIds = filtered.map(v => v.id!).filter(Boolean);
    const allEntries = allVoucherIds.length > 0
      ? await db.voucherEntries.where('voucherId').anyOf(allVoucherIds).toArray()
      : [];
    const entriesByVoucher = new Map<number, typeof allEntries>();
    for (const e of allEntries) {
      if (!entriesByVoucher.has(e.voucherId)) entriesByVoucher.set(e.voucherId, []);
      entriesByVoucher.get(e.voucherId)!.push(e);
    }

    const monthlyData = new Map<string, { count: number, cancelled: number, totalAmount: number }>();

    const [fYear, fMonth, fDay] = fromDate.split('-');
    let current = new Date(parseInt(fYear), parseInt(fMonth) - 1, 1);
    const [tYear, tMonth, tDay] = toDate.split('-');
    const end = new Date(parseInt(tYear), parseInt(tMonth) - 1, parseInt(tDay));
    
    while (current <= end || (current.getFullYear() === end.getFullYear() && current.getMonth() === end.getMonth())) {
      const monthStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
      monthlyData.set(monthStr, { count: 0, cancelled: 0, totalAmount: 0 });
      current.setMonth(current.getMonth() + 1);
    }

    for (const v of filtered) {
      const monthStr = v.date.substring(0, 7);
      if (monthlyData.has(monthStr)) {
        const data = monthlyData.get(monthStr)!;
        data.count += 1;

        let voucherAmount = 0;
        const entries = entriesByVoucher.get(v.id!) || [];
        for (const entry of entries) {
          if (entry.type === 'Dr') {
            let amount = entry.amount;
            const curId = entry.currencyId || v.currencyId;
            if (curId) {
               const fx = currencyMap.get(curId);
               if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
                  amount = entry.amount * fx.exchangeRate;
               }
            }
            voucherAmount += amount;
          }
        }
        data.totalAmount += voucherAmount;
      }
    }

    const result = Array.from(monthlyData.entries()).map(([monthStr, data]) => {
      const [year, month] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      return {
        period: monthStr,
        name: monthName,
        count: data.count,
        cancelled: data.cancelled,
        totalAmount: data.totalAmount
      };
    });

    return result;
  }

  /**
   * Generates a detailed list of vouchers for a specific month and type (Voucher Register)
   */
  static async generateVoucherRegister(companyId: number, voucherType: string, period: string) {
    // period is 'YYYY-MM'
    const allVouchers = await db.vouchers
      .where('companyId').equals(companyId)
      .filter(v => v.type === voucherType && !v.deleted && v.date.startsWith(period))
      .toArray();
    const filtered = allVouchers;

    const currencies = await db.currencies.where('companyId').equals(companyId).toArray();
    const currencyMap = new Map(currencies.map(c => [c.id, c]));

    // Pre-load voucherType metadata for custom-type party detection
    const voucherTypeRecord = await db.voucherTypes.where('[companyId+name]').equals([companyId, voucherType]).first();
    // CHARGE_GROUPS: entries in these groups are taxes/freight, not the party
    const CHARGE_GROUPS = new Set(['Duties & Taxes', 'Direct Expenses', 'Indirect Expenses', 'Direct Income', 'Indirect Income']);
    const allGroups = await db.groups.where('companyId').equals(companyId).toArray();
    const groupMap = new Map(allGroups.map(g => [g.id, g.name]));

    const result = [];
    for (const v of filtered) {
      const entries = await db.voucherEntries.where('voucherId').equals(v.id!).toArray();
      let particulars = 'Unknown';
      let debit = '';
      let credit = '';

      if (entries.length > 0) {
        const drEntries = entries.filter(e => e.type === 'Dr');
        const crEntries = entries.filter(e => e.type === 'Cr');

        // Determine party side via voucherType DB record (supports custom types).
        // Sales/Out-type → party is Dr; Purchase/In-type → party is Cr.
        // Fallback to system-type name matching for unregistered types.
        let partyDrCr: 'Dr' | 'Cr' = 'Dr';
        if (voucherTypeRecord) {
          partyDrCr = voucherTypeRecord.stockDirection === 'Out' ? 'Dr' : 'Cr';
        } else {
          if (voucherType === 'Purchase' || voucherType === 'Payment') partyDrCr = 'Cr';
        }

        const sideEntries = partyDrCr === 'Dr' ? drEntries : crEntries;

        // Batch-load ledgers to identify charge entries (taxes, freight, etc.)
        const ledgerIds = [...new Set(entries.map(e => e.ledgerId))];
        const entryLedgers = await Promise.all(ledgerIds.map(id => db.ledgers.get(id)));
        const entryLedgerMap = new Map(entryLedgers.filter(Boolean).map(l => [l!.id, l!]));

        const isCharge = (e: any) => {
          const led = entryLedgerMap.get(e.ledgerId);
          if (!led) return false;
          const gName = led.groupId ? groupMap.get(led.groupId) : '';
          return gName ? CHARGE_GROUPS.has(gName) : false;
        };

        // Party = first non-charge entry on the correct Dr/Cr side
        const partyEntry =
          sideEntries.find(e => !isCharge(e)) ||
          sideEntries[0] ||
          entries[0];

        if (partyEntry) {
          const ledger = entryLedgerMap.get(partyEntry.ledgerId);
          particulars = ledger?.name || 'Unknown';
        }

        let totalDr = 0;
        let totalCr = 0;

        for (const e of entries) {
           let amount = e.amount;
           const curId = e.currencyId || v.currencyId;
           if (curId) {
             const fx = currencyMap.get(curId);
             if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
                amount = e.amount * fx.exchangeRate;
             }
           }
           if (e.type === 'Dr') totalDr += amount;
           if (e.type === 'Cr') totalCr += amount;
        }

        if (totalDr > 0) debit = totalDr.toFixed(2);
        if (totalCr > 0) credit = totalCr.toFixed(2);
      }

      result.push({
        id: v.id,
        date: v.date,
        particulars,
        vchType: v.type,
        vchNo: v.voucherNumber ? v.voucherNumber : (v.sysId || ''),
        debit,
        credit,
        name: particulars
      });
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }

  /**
   * Generates voucher list for a specific stock item
   */
  static async generateStockItemVouchers(stockItemId: number, fromDate: string, toDate: string) {
    const item = await db.stockItems.get(stockItemId);
    if (!item) throw new Error('Stock Item not found');

    const inventoryEntries = await db.inventoryEntries.where('stockItemId').equals(stockItemId).toArray();
    const voucherIds = [...new Set(inventoryEntries.map(ie => ie.voucherId))];
    const vouchers = voucherIds.length > 0 ? await db.vouchers.where('id').anyOf(voucherIds).toArray() : [];
    
    const voucherTypes = await db.voucherTypes.where('companyId').equals(item.companyId).toArray();
    const voucherTypeMap = new Map(voucherTypes.map(vt => [vt.name, vt]));

    const currencies = await db.currencies.where('companyId').equals(item.companyId).toArray();
    const currencyMap = new Map(currencies.map(c => [c.id, c]));

    const result: any[] = [];
    
    let openingQty = item.openingBalance || 0;
    let openingValue = item.openingValue || 0;

    const vouchersBefore = vouchers.filter(v => v.date < fromDate && !v.deleted);
    for (const v of vouchersBefore) {
      const ie = inventoryEntries.find(e => e.voucherId === v.id);
      if (!ie) continue;

      const vt = voucherTypeMap.get(v.type);
      let isInward = v.type === 'Purchase' || v.type === 'Receipt';
      if (vt && vt.affectsStock) {
        if (vt.stockDirection === 'In') isInward = true;
        else if (vt.stockDirection === 'Out') isInward = false;
      }

      openingQty += isInward ? ie.quantity : -ie.quantity;

      let entryAmount = ie.amount;
      const curId = ie.currencyId || v.currencyId;
      if (curId) {
        const fx = currencyMap.get(curId);
        if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
          entryAmount = ie.amount * fx.exchangeRate;
        }
      }
      openingValue += isInward ? entryAmount : -entryAmount;
    }

    let runningQty = openingQty;

    // Add opening balance
    result.push({
      date: '',
      particulars: 'Opening Balance',
      vchType: '',
      vchNo: '',
      quantity: openingQty,
      rate: openingQty ? openingValue / openingQty : 0,
      value: openingValue,
      type: 'Inward',
      isOpening: true
    });

    const filteredVouchers = vouchers
      .filter(v => v.date >= fromDate && v.date <= toDate && !v.deleted)
      .sort((a, b) => a.date.localeCompare(b.date));

    for (const v of filteredVouchers) {
      const ie = inventoryEntries.find(e => e.voucherId === v.id);
      if (!ie) continue;

      const vt = voucherTypeMap.get(v.type);
      let isInward = v.type === 'Purchase' || v.type === 'Receipt'; // Fallback
      if (vt && vt.affectsStock) {
        if (vt.stockDirection === 'In') isInward = true;
        else if (vt.stockDirection === 'Out') isInward = false;
      }

      runningQty += isInward ? ie.quantity : -ie.quantity;

      // Find ledger name for particulars by querying voucher entries
      const entries = await db.voucherEntries.where('voucherId').equals(v.id!).toArray();
      const oppositeEntry = entries.find(e => e.type === (isInward ? 'Cr' : 'Dr'));
      let ledgerName = v.type;
      if (oppositeEntry) {
        const ledger = await db.ledgers.get(oppositeEntry.ledgerId);
        if (ledger) ledgerName = ledger.name;
      }

      let entryAmount = ie.amount;
      const curId = ie.currencyId || v.currencyId;
      if (curId) {
        const fx = currencyMap.get(curId);
        if (fx && !fx.isBaseCurrency && fx.exchangeRate) {
          entryAmount = ie.amount * fx.exchangeRate;
        }
      }

      let entryRate = ie.rate;
      if (ie.quantity && entryAmount !== ie.amount) {
         entryRate = entryAmount / ie.quantity;
      }

      result.push({
        id: v.id,
        date: v.date,
        particulars: ledgerName,
        vchType: v.type,
        vchNo: v.voucherNumber,
        quantity: ie.quantity,
        rate: entryRate,
        value: entryAmount,
        type: isInward ? 'Inward' : 'Outward'
      });
    }

    return { item, vouchers: result, closingQty: runningQty };
  }

  /**
   * Generates a running-balance ledger report for a given ledger and date range.
   * Mirrors the logic in LedgerVouchersView so print views (LedgerReport.tsx) get
   * consistent data without re-implementing the balance accumulation inline.
   */
  static async generateLedgerReport(
    ledgerId: number,
    fromDate: string,
    toDate: string
  ) {
    const ledger = await db.ledgers.get(ledgerId);
    if (!ledger) return null;

    const entries = await db.voucherEntries.where('ledgerId').equals(ledgerId).toArray();

    // Pre-fetch vouchers and sort entries chronologically
    const voucherCache = new Map<number, any>();
    for (const entry of entries) {
      if (!voucherCache.has(entry.voucherId)) {
        const v = await db.vouchers.get(entry.voucherId);
        if (v) voucherCache.set(entry.voucherId, v);
      }
    }
    const sortedEntries = [...entries].sort((a, b) => {
      const va = voucherCache.get(a.voucherId);
      const vb = voucherCache.get(b.voucherId);
      if (!va || !vb) return 0;
      return va.date.localeCompare(vb.date);
    });

    const CHARGE_GROUPS = new Set([
      'Duties & Taxes', 'Direct Expenses', 'Indirect Expenses',
      'Direct Income', 'Indirect Income'
    ]);

    // ── Pre-load all data needed for particulars resolution (eliminates N+1) ──
    const companyId = ledger.companyId;
    const allVoucherIds = [...voucherCache.keys()];

    // All entries for every related voucher in one batch query
    const allSiblingEntries = allVoucherIds.length > 0
      ? await db.voucherEntries.where('voucherId').anyOf(allVoucherIds).toArray()
      : [];
    const siblingEntriesByVoucher = new Map<number, typeof allSiblingEntries>();
    for (const e of allSiblingEntries) {
      if (!siblingEntriesByVoucher.has(e.voucherId)) siblingEntriesByVoucher.set(e.voucherId, []);
      siblingEntriesByVoucher.get(e.voucherId)!.push(e);
    }

    // All ledgers for this company in one query
    const allCompanyLedgers = await db.ledgers.where('companyId').equals(companyId).toArray();
    const ledgerMapGlobal = new Map(allCompanyLedgers.map(l => [l.id, l]));

    // All groups for this company in one query
    const allGroups = await db.groups.where('companyId').equals(companyId).toArray();
    const groupMapGlobal = new Map(allGroups.map(g => [g.id, g.name]));

    // All voucher types for this company in one query
    const allVoucherTypes = await db.voucherTypes.where('companyId').equals(companyId).toArray();
    const voucherTypeMapGlobal = new Map(allVoucherTypes.map(vt => [vt.name, vt]));

    const isCharge = (e: any) => {
      const led = ledgerMapGlobal.get(e.ledgerId);
      if (!led) return false;
      const gName = (led as any).groupId ? groupMapGlobal.get((led as any).groupId) : '';
      return gName ? CHARGE_GROUPS.has(gName) : false;
    };

    let runningBalance = ledger.balanceType === 'Dr'
      ? ledger.openingBalance
      : -ledger.openingBalance;

    const result: any[] = [];

    for (const entry of sortedEntries) {
      const voucher = voucherCache.get(entry.voucherId);
      if (!voucher) continue;

      if (voucher.date < fromDate) {
        // Pre-period: accumulate into opening balance
        if (entry.type === 'Dr') runningBalance += entry.amount;
        if (entry.type === 'Cr') runningBalance -= entry.amount;
        continue;
      }
      if (voucher.date > toDate) continue;

      // Determine particulars using pre-loaded data (zero extra DB queries)
      const allVoucherEntries = siblingEntriesByVoucher.get(voucher.id!) || [];

      let particulars = 'Unknown';
      if (allVoucherEntries.length > 0) {
        const voucherTypeRecord = voucherTypeMapGlobal.get(voucher.type);

        let partyDrCr: 'Dr' | 'Cr' | null = null;
        if (voucherTypeRecord?.stockDirection === 'Out') partyDrCr = 'Dr';
        else if (voucherTypeRecord?.stockDirection === 'In') partyDrCr = 'Cr';
        else if (['Sales', 'Credit Note'].includes(voucher.type)) partyDrCr = 'Dr';
        else if (['Purchase', 'Debit Note'].includes(voucher.type)) partyDrCr = 'Cr';
        else if (voucher.type === 'Receipt') partyDrCr = 'Cr';
        else if (voucher.type === 'Payment') partyDrCr = 'Dr';

        let targetEntry = allVoucherEntries.find((e: any) => e.id !== entry.id);
        if (partyDrCr) {
          targetEntry =
            allVoucherEntries.find((e: any) => e.type === partyDrCr && !isCharge(e) && e.id !== entry.id) ||
            allVoucherEntries.find((e: any) => e.type === partyDrCr && e.id !== entry.id) ||
            targetEntry;
        }

        if (targetEntry) {
          const led = ledgerMapGlobal.get((targetEntry as any).ledgerId);
          particulars = (led as any)?.name || 'Unknown';
        }
      }

      if (entry.type === 'Dr') runningBalance += entry.amount;
      if (entry.type === 'Cr') runningBalance -= entry.amount;

      result.push({
        id: voucher.id,
        date: voucher.date,
        particulars,
        vchType: voucher.type,
        vchNo: voucher.voucherNumber,
        refNo: voucher.referenceNumber || '',
        narration: voucher.narration || '',
        debit: entry.type === 'Dr' ? entry.amount.toFixed(2) : '',
        credit: entry.type === 'Cr' ? entry.amount.toFixed(2) : '',
        balance: Math.abs(runningBalance).toFixed(2),
        balanceType: runningBalance >= 0 ? 'Dr' : 'Cr',
      });
    }

    return {
      ledger,
      vouchers: result,
      openingBalance: Math.abs(
        ledger.balanceType === 'Dr' ? ledger.openingBalance : -ledger.openingBalance
      ).toFixed(2),
      openingType: (ledger.balanceType === 'Dr' ? ledger.openingBalance : -ledger.openingBalance) >= 0 ? 'Dr' : 'Cr',
      closingBalance: Math.abs(runningBalance).toFixed(2),
      closingType: runningBalance >= 0 ? 'Dr' : 'Cr',
    };
  }
}
